import React, { useRef, useState } from 'react';

interface FileUploadProps {
  acceptedTypes: string[];
  onFileSelect: (file: File) => void;
  maxSize?: number; // in bytes
}

const FileUpload: React.FC<FileUploadProps> = ({ 
  acceptedTypes, 
  onFileSelect, 
  maxSize = 5 * 1024 * 1024 // 5MB default
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const validateFile = (file: File): boolean => {
    setError('');
    const file_extension = file.name.split('.').pop()?.toLowerCase(); // Get the extension from the file name
    
    // Validate the file type
    const acceptedExtensions = ['csv', 'xlsx', 'xls']; // Define accepted file extensions
    if (!file_extension || !acceptedExtensions.includes(file_extension)) {
        setError('Invalid file type. Please upload a CSV or Excel file.');
        return false; // Return false if the file type is not valid
    }
    
    if (file.size > maxSize) {
      setError(`The file is too large. Maximum size is ${maxSize / 1024 / 1024}MB. Next update will increase the max file size.`);
      return false;
    }
    
    return true;
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (validateFile(file)) {
        onFileSelect(file);
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (validateFile(file)) {
        onFileSelect(file);
      }
    }
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  return (
    <div className="file-upload-container">
      <label htmlFor="file-upload" className="sr-only">Choose a file</label>
      <div
        className={`file-upload-area ${dragActive ? 'drag-active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={handleClick}
        aria-label="Click or drag and drop to upload a file"
      >

        <input
          id="file-upload"
          ref={inputRef}
          type="file"
        //   accept={acceptedTypes.join(',')}
          onChange={handleChange}
          style={{ display: 'none' }}
          aria-label="File upload input"
        />
        <div className="file-upload-content">
          <p>Drag and drop your file here or click to select</p>
          <p className="file-types">Supported file types: {acceptedTypes.join(', ')}</p>
        </div>
      </div>
      {error && (
        <p className="error-message" role="alert" aria-live="polite">
          {error}
        </p>
      )}
    </div>
  );
};

export default FileUpload; 